import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  getNextQuestion,
  submitAnswer,
  getHint,
  startSession,
  endSession,
  getCalibrationInfo,
  getStrugglingLOs,
  updateAssignmentProgress,
  startAssignment,
  CalibrationInfo,
  StrugglingLO,
  Question as APIQuestion,
  SubmissionResult as APISubmissionResult
} from '../services/api';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { AIHelpPanel } from '../components/AIHelpPanel';
import { MathRenderer } from '../components/shared/MathRenderer';
import { DesmosEmbed } from '../components/shared/DesmosEmbed';
import { FeedbackWidget } from '../components/practice/FeedbackWidget';
import { storage } from '../services/storage';

const DEFAULT_QUESTIONS_PER_SESSION = 10;
const QUICK_QUESTIONS_PER_SESSION = 5;

interface Question extends APIQuestion {}
interface SubmissionResult extends APISubmissionResult {}

// Format review date relative to today
const formatReviewDate = (dateStr: string): string => {
  const reviewDate = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((reviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `In ${diffDays} days`;
  if (diffDays < 30) return `In ${Math.round(diffDays / 7)} week${diffDays >= 14 ? 's' : ''}`;
  return reviewDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const PracticePage: React.FC = () => {
  const { unit, lesson } = useParams<{ unit: string; lesson: string }>();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') || 'practice';
  const assignmentId = searchParams.get('assignment_id');
  const STUDENT_ID = storage.getStudentId();
  const isRemediation = mode === 'remediation';
  const isChallenge = mode === 'challenge';
  const isQuickPractice = mode === 'quick';
  const isAssignment = mode === 'assignment' && !!assignmentId;

  // unit=0, lesson=0 is a sentinel for "no specific unit" (quick practice)
  const rawUnit = unit ? parseInt(unit, 10) : undefined;
  const rawLesson = lesson ? parseInt(lesson, 10) : undefined;
  const unitNum = rawUnit === 0 ? undefined : rawUnit;
  const lessonNum = rawLesson === 0 ? undefined : rawLesson;
  const QUESTIONS_PER_SESSION = isQuickPractice ? QUICK_QUESTIONS_PER_SESSION : DEFAULT_QUESTIONS_PER_SESSION;

  const [question, setQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingQuestion, setFetchingQuestion] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmissionResult | null>(null);
  const [answer, setAnswer] = useState('');
  const [startTime, setStartTime] = useState(Date.now());
  const [hintUsed, setHintUsed] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [calibration, setCalibration] = useState<CalibrationInfo | null>(null);
  const [showAdaptiveInfo, setShowAdaptiveInfo] = useState(false);
  const [strugglingLOs, setStrugglingLOs] = useState<StrugglingLO[]>([]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [firstAttemptAnswer, setFirstAttemptAnswer] = useState('');
  const [isSimilarProblem, setIsSimilarProblem] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const statsRef = useRef(stats); // Keep ref for cleanup
  statsRef.current = stats;
  const submittingRef = useRef(false); // Guard against double-submissions

  // Sound effects
  const { enabled: soundEnabled, toggle: toggleSound, playCorrect, playIncorrect, playHint, playComplete } = useSoundEffects();

  const fetchQuestion = async () => {
    setLoading(true);
    setFetchingQuestion(true);
    setError(null);
    setResult(null);
    setAnswer('');
    setHint(null);
    setHintUsed(false);
    setAttemptNumber(1);
    setFirstAttemptAnswer('');
    setIsSimilarProblem(false);

    try {
      // Use remediation mode if that's what we're in, adaptive for quick practice, practice for everything else
      const fetchMode = isRemediation ? 'remediation' : isQuickPractice ? 'adaptive' : 'practice';
      const challengeDifficulty = isChallenge ? 3 : undefined;
      const q = await getNextQuestion(STUDENT_ID, fetchMode, unitNum, lessonNum, challengeDifficulty);
      setQuestion(q);
      setStartTime(Date.now());
    } catch (err: any) {
      if (isRemediation && err.response?.status === 404) {
        // No more remediation questions - session complete
        setSessionComplete(true);
      } else {
        setError(err.message || 'Failed to load question');
      }
    } finally {
      setLoading(false);
      setFetchingQuestion(false);
    }
  };

  // Initialize session and fetch first question
  useEffect(() => {
    let currentSessionId: string | null = null;

    const initSession = async () => {
      try {
        const sessionType = isAssignment ? 'assignment' : isRemediation ? 'remediation' : isQuickPractice ? 'quick_practice' : 'practice';
        const promises: Promise<any>[] = [
          startSession(STUDENT_ID, sessionType, unitNum, lessonNum),
          isQuickPractice ? Promise.resolve(null) : getCalibrationInfo(STUDENT_ID, unitNum).catch(() => null),
        ];

        // Start assignment if needed
        if (isAssignment && assignmentId) {
          promises.push(startAssignment(assignmentId, STUDENT_ID).catch(() => null));
        }

        // Fetch struggling LOs for remediation mode
        if (isRemediation) {
          promises.push(getStrugglingLOs(STUDENT_ID).catch(() => ({ struggling_los: [] })));
        }

        const results = await Promise.all(promises);
        const session = results[0];
        const cal = results[1];

        currentSessionId = session.id;
        setSessionId(session.id);
        if (cal) setCalibration(cal);

        if (isRemediation && results[2]) {
          setStrugglingLOs(results[2].struggling_los || []);
        }
      } catch (err) {
        console.error('Failed to start session:', err);
      }
      fetchQuestion();
    };

    initSession();

    // Cleanup: end session when leaving
    return () => {
      if (currentSessionId && statsRef.current.attempted > 0) {
        endSession(currentSessionId, statsRef.current.attempted, statsRef.current.correct)
          .catch(err => console.error('Failed to end session:', err));
      }
    };
  }, [unitNum, lessonNum, isRemediation, isAssignment, isQuickPractice]);

  // Auto-focus input when new question loads
  useEffect(() => {
    if (question && !result) {
      // Use a short timeout to ensure the input is rendered and nothing steals focus
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [question, result]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      // Check if typing in AI chat (don't handle Enter for that)
      const isInAIChat = target.closest('.ai-help-panel') !== null;
      if (isInAIChat) {
        return; // Let the AI panel handle its own Enter key
      }

      // Enter in input submits answer
      if (e.key === 'Enter' && isTyping && !result && answer.trim()) {
        e.preventDefault();
        handleSubmit();
        return;
      }

      // Enter when retry mode (allow_retry result) → trigger retry
      if (e.key === 'Enter' && result?.allow_retry && !isTyping) {
        e.preventDefault();
        handleRetry();
        return;
      }

      // Enter when final result shown goes to next (only if not typing)
      if (e.key === 'Enter' && result && !result.allow_retry && !isTyping) {
        e.preventDefault();
        if (!isSimilarProblem && stats.attempted >= QUESTIONS_PER_SESSION) {
          setSessionComplete(true);
        } else {
          fetchQuestion();
        }
        return;
      }

      // Other shortcuts only when not typing
      if (isTyping) return;

      switch (e.key.toLowerCase()) {
        case 'h':
          // H for hint
          if (question?.hint_available && !hint && !result) {
            e.preventDefault();
            handleHint();
          }
          break;
        case 's':
          // S to toggle sound
          e.preventDefault();
          toggleSound();
          break;
        case '?':
          // ? to toggle shortcuts help
          e.preventDefault();
          setShowShortcuts(prev => !prev);
          break;
        case 'escape':
          // Escape to close shortcuts help
          setShowShortcuts(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [result, stats.attempted, answer, question, hint, toggleSound, isSimilarProblem]);

  const handleSubmit = async () => {
    if (!question || !answer.trim()) return;
    if (submittingRef.current) return; // Prevent double-submission
    submittingRef.current = true;

    const timeMs = Date.now() - startTime;

    try {
      const res = await submitAnswer(
        STUDENT_ID,
        question.question_id,
        answer,
        timeMs,
        hintUsed,
        attemptNumber
      );

      // If backend says allow_retry → enter retry mode, don't count stats yet
      if (res.allow_retry) {
        setResult(res);
        setFirstAttemptAnswer(answer);
        playIncorrect();
        return;
      }

      setResult(res);

      // Only count toward stats on final submission (not during retry mode)
      if (!isSimilarProblem) {
        const newStats = {
          attempted: stats.attempted + 1,
          correct: stats.correct + (res.correct ? 1 : 0)
        };
        setStats(newStats);

        // Update assignment progress if in assignment mode
        if (isAssignment && assignmentId) {
          updateAssignmentProgress(
            assignmentId,
            STUDENT_ID,
            newStats.attempted,
            newStats.correct
          ).catch((err) => console.error('Failed to update assignment:', err));
        }
      }

      // Play sound effect
      if (res.correct) {
        playCorrect();
      } else {
        playIncorrect();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    } finally {
      submittingRef.current = false;
    }
  };

  const handleRetry = () => {
    setAnswer('');
    setAttemptNumber(2);
    setResult(null);
    setStartTime(Date.now());
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleShowAnswer = async () => {
    // Submit with attempt_number=2 and the original wrong answer to trigger
    // full SR update and get explanation + correct answer back
    if (!question) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    const timeMs = Date.now() - startTime;
    try {
      const res = await submitAnswer(
        STUDENT_ID,
        question.question_id,
        firstAttemptAnswer || answer, // use first attempt answer
        timeMs,
        hintUsed,
        2 // attempt_number = 2
      );
      setResult(res);

      if (!isSimilarProblem) {
        const newStats = {
          attempted: stats.attempted + 1,
          correct: stats.correct + (res.correct ? 1 : 0)
        };
        setStats(newStats);

        if (isAssignment && assignmentId) {
          updateAssignmentProgress(
            assignmentId,
            STUDENT_ID,
            newStats.attempted,
            newStats.correct
          ).catch((err) => console.error('Failed to update assignment:', err));
        }
      }

      playIncorrect();
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    } finally {
      submittingRef.current = false;
    }
  };

  const fetchSimilarQuestion = async () => {
    if (!question) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setAnswer('');
    setHint(null);
    setHintUsed(false);
    setAttemptNumber(1);
    setFirstAttemptAnswer('');
    setIsSimilarProblem(true);

    try {
      const fetchMode = isRemediation ? 'remediation' : isQuickPractice ? 'adaptive' : 'practice';
      const q = await getNextQuestion(STUDENT_ID, fetchMode, unitNum, lessonNum, undefined, question.question_id);
      setQuestion(q);
      setStartTime(Date.now());
    } catch (err: any) {
      setError(err.message || 'Failed to load similar question');
    } finally {
      setLoading(false);
    }
  };

  const handleHint = async () => {
    if (!question) return;

    try {
      const hintText = await getHint(question.question_id, STUDENT_ID);
      setHint(hintText);
      setHintUsed(true);
      playHint();
    } catch (err) {
      console.error('Failed to get hint:', err);
    }
  };

  const handleNext = async () => {
    if (!isSimilarProblem && stats.attempted >= QUESTIONS_PER_SESSION) {
      // End the session when complete
      if (sessionId) {
        try {
          await endSession(sessionId, stats.attempted, stats.correct);
        } catch (err) {
          console.error('Failed to end session:', err);
        }
      }
      setSessionComplete(true);
      playComplete();
    } else {
      fetchQuestion();
    }
  };

  const startNewSession = async () => {
    // Start a fresh session
    try {
      const sessionType = isRemediation ? 'remediation' : isQuickPractice ? 'quick_practice' : 'practice';
      const session = await startSession(STUDENT_ID, sessionType, unitNum, lessonNum);
      setSessionId(session.id);

      // Refresh struggling LOs for remediation mode
      if (isRemediation) {
        const losData = await getStrugglingLOs(STUDENT_ID).catch(() => ({ struggling_los: [] }));
        setStrugglingLOs(losData.struggling_los || []);
      }
    } catch (err) {
      console.error('Failed to start new session:', err);
    }
    setStats({ attempted: 0, correct: 0 });
    setSessionComplete(false);
    fetchQuestion();
  };

  if (loading && !question) {
    return <div className="loading">{isRemediation ? 'Finding questions for your weak areas...' : 'Loading...'}</div>;
  }

  if (error && !question) {
    // Special case: no struggling LOs in remediation mode
    if (isRemediation && (error.includes('404') || error.includes('No questions'))) {
      return (
        <div className="session-complete">
          <div className="complete-icon">🎉</div>
          <h2>Great Job!</h2>
          <p className="remediation-message">
            You don't have any struggling areas right now. Keep up the great work!
          </p>
          <div className="complete-actions">
            <Link to="/practice" className="primary-btn">
              Practice Lessons
            </Link>
            <Link to="/" className="secondary-btn">
              Back to Dashboard
            </Link>
          </div>
        </div>
      );
    }

    if (error.includes('No questions') || error.includes('404')) {
      return (
        <div className="session-complete">
          <div className="complete-icon">📚</div>
          <h2>No Practice Questions Available</h2>
          <p>Practice questions for this curriculum are still being prepared. Please check back soon!</p>
          <div className="complete-actions">
            <Link to="/" className="primary-btn">
              Back to Dashboard
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="error-page">
        <h2>Oops!</h2>
        <p>{error}</p>
        <button onClick={fetchQuestion}>Try Again</button>
      </div>
    );
  }

  // Show loading while fetching next question to prevent flash
  if (fetchingQuestion) {
    return <div className="loading">{isRemediation ? 'Finding questions for your weak areas...' : 'Loading next question...'}</div>;
  }

  if (sessionComplete) {
    const accuracy = stats.attempted > 0
      ? Math.round((stats.correct / stats.attempted) * 100)
      : 0;

    // Special case: remediation ended with 0 questions (no weak areas or no questions available)
    if (isRemediation && stats.attempted === 0) {
      return (
        <div className="session-complete">
          <div className="complete-icon">🎉</div>
          <h2>Great Job!</h2>
          <p className="remediation-message">
            {strugglingLOs.length === 0
              ? "You don't have any struggling areas right now. Keep up the great work!"
              : "No practice questions are available for your weak areas at this time. Try practicing some lessons first."}
          </p>
          <div className="complete-actions">
            <Link to="/practice" className="primary-btn">
              Practice Lessons
            </Link>
            <Link to="/" className="secondary-btn">
              Back to Dashboard
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="session-complete">
        <div className="complete-icon">
          {accuracy >= 80 ? '🎉' : accuracy >= 60 ? '👍' : '💪'}
        </div>
        <h2>{isQuickPractice ? 'Quick Practice Complete!' : isRemediation ? 'Remediation Complete!' : 'Session Complete!'}</h2>
        {isRemediation && (
          <p className="remediation-message">
            Great work focusing on your weak areas! Keep practicing to build mastery.
          </p>
        )}
        <div className="session-summary">
          <div className="stat-box">
            <span className="stat-value">{stats.attempted}</span>
            <span className="stat-label">Questions</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{stats.correct}</span>
            <span className="stat-label">Correct</span>
          </div>
          <div className="stat-box">
            <span className="stat-value">{accuracy}%</span>
            <span className="stat-label">Accuracy</span>
          </div>
        </div>
        <div className="complete-actions">
          <button className="primary-btn" onClick={startNewSession}>
            {isQuickPractice ? 'Play Again' : isRemediation ? 'Continue Remediation' : 'Practice More'}
          </button>
          <Link to="/practice" className="secondary-btn">
            Choose Another Lesson
          </Link>
          <Link to="/" className="secondary-btn">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`practice-page ${isRemediation ? 'remediation-mode' : ''} ${isAssignment ? 'assignment-mode' : ''} ${isQuickPractice ? 'quick-practice-mode' : ''}`}>
      <div className="practice-header">
        <div className="practice-header-top">
          {isAssignment ? (
            <>
              <h2>
                <span className="assignment-icon">&#128221;</span>
                Assignment Mode
              </h2>
              <div className="session-stats">
                <button
                  className="sound-toggle"
                  onClick={toggleSound}
                  title={soundEnabled ? 'Sound on (press S to toggle)' : 'Sound off (press S to toggle)'}
                >
                  {soundEnabled ? '\uD83D\uDD0A' : '\uD83D\uDD07'}
                </button>
                Question {stats.attempted + 1} | {stats.correct} correct
              </div>
            </>
          ) : isRemediation ? (
            <>
              <h2>
                <span className="remediation-icon">🎯</span>
                Practicing Weak Areas
              </h2>
              <div className="session-stats">
                <button
                  className="sound-toggle"
                  onClick={toggleSound}
                  title={soundEnabled ? 'Sound on (press S to toggle)' : 'Sound off (press S to toggle)'}
                >
                  {soundEnabled ? '🔊' : '🔇'}
                </button>
                Question {stats.attempted + 1} | {stats.correct} correct
              </div>
            </>
          ) : isQuickPractice ? (
            <>
              <h2>
                <span className="quick-practice-bolt">&#9889;</span>
                Quick Practice
              </h2>
              <div className="session-stats">
                <button
                  className="sound-toggle"
                  onClick={toggleSound}
                  title={soundEnabled ? 'Sound on (press S to toggle)' : 'Sound off (press S to toggle)'}
                >
                  {soundEnabled ? '\uD83D\uDD0A' : '\uD83D\uDD07'}
                </button>
                Question {stats.attempted + 1} of {QUESTIONS_PER_SESSION} | {stats.correct} correct
              </div>
            </>
          ) : isChallenge ? (
            <>
              <h2>
                <span className="remediation-icon">&#9889;</span>
                Challenge Mode {unitNum ? `— Unit ${unitNum}` : ''}
              </h2>
              <div className="session-stats">
                <button
                  className="sound-toggle"
                  onClick={toggleSound}
                  title={soundEnabled ? 'Sound on (press S to toggle)' : 'Sound off (press S to toggle)'}
                >
                  {soundEnabled ? '🔊' : '🔇'}
                </button>
                Question {stats.attempted + 1} of {QUESTIONS_PER_SESSION} | {stats.correct} correct
              </div>
            </>
          ) : (
            <>
              <h2>Unit {unitNum}, Lesson {lessonNum}</h2>
              <div className="session-stats">
                <button
                  className="sound-toggle"
                  onClick={toggleSound}
                  title={soundEnabled ? 'Sound on (press S to toggle)' : 'Sound off (press S to toggle)'}
                >
                  {soundEnabled ? '🔊' : '🔇'}
                </button>
                Question {stats.attempted + 1} of {QUESTIONS_PER_SESSION} | {stats.correct} correct
              </div>
            </>
          )}
        </div>

        {isRemediation && strugglingLOs.length > 0 && (
          <div className="remediation-info-bar">
            <div className="remediation-focus">
              <span className="label">Focusing on:</span>
              {strugglingLOs.slice(0, 4).map(lo => (
                <span key={lo.lo} className="focus-lo">
                  {lo.description || lo.lo}
                  <span className="lo-accuracy">({lo.accuracy}%)</span>
                </span>
              ))}
              {strugglingLOs.length > 4 && (
                <span className="more-los">+{strugglingLOs.length - 4} more</span>
              )}
            </div>
          </div>
        )}

        {!isRemediation && !isQuickPractice && calibration && (
          <div className="adaptive-info-bar">
            <button
              className="adaptive-toggle"
              onClick={() => setShowAdaptiveInfo(!showAdaptiveInfo)}
            >
              Difficulty: {calibration.target_difficulty.toFixed(1)}/5
              {calibration.struggling_los.length > 0 && (
                <span className="struggling-badge">
                  {calibration.struggling_los.length} areas need work
                </span>
              )}
              <span className="toggle-icon">{showAdaptiveInfo ? '▲' : '▼'}</span>
            </button>

            {showAdaptiveInfo && (
              <div className="adaptive-details">
                <div className="adaptive-stat">
                  <span className="label">Recent Accuracy:</span>
                  <span className="value">{calibration.recent_accuracy?.toFixed(0) || '--'}%</span>
                </div>
                {calibration.struggling_los.length > 0 && (
                  <div className="struggling-list">
                    <span className="label">Focus Areas:</span>
                    {calibration.struggling_los.slice(0, 3).map(lo => (
                      <span key={lo.lo} className="struggling-lo">
                        {lo.description || lo.lo} ({lo.accuracy}%)
                      </span>
                    ))}
                  </div>
                )}
                {calibration.mastered_los.length > 0 && (
                  <div className="mastered-list">
                    <span className="label">Mastered:</span>
                    {calibration.mastered_los.slice(0, 3).map(lo => (
                      <span key={typeof lo === 'string' ? lo : lo.lo} className="mastered-lo">
                        {typeof lo === 'string' ? lo : (lo.description || lo.lo)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {question && (
        <div className="question-card">
          <div className="question-header">
            <div className="question-header-left">
              <span className="question-id">{question.question_id}</span>
              {question.is_review && (
                <span className="review-badge">Review</span>
              )}
              {question.is_remediation && question.target_lo && (
                <span className="remediation-badge" title={`Accuracy: ${question.lo_accuracy}%`}>
                  {question.target_lo}
                </span>
              )}
            </div>
            <span className="time-limit">⏱️ {question.time_limit_seconds}s</span>
          </div>

          <div className="question-prompt">
            <MathRenderer content={question.prompt} />
          </div>

          {question.requires_desmos && question.desmos_expressions && question.desmos_expressions.length > 0 && (
            <div className="desmos-section">
              <DesmosEmbed
                type={question.desmos_type || 'graphing'}
                expressions={question.desmos_expressions}
              />
            </div>
          )}

          <div className="answer-section">
            {question.answer_type === 'multiple_choice' && question.choices && question.choices.length > 0 ? (
              <div className="mc-choices">
                {question.choices.map((choice) => (
                  <button
                    key={choice.value}
                    className={`mc-choice-btn ${answer === choice.value ? 'selected' : ''}`}
                    onClick={() => { setAnswer(choice.value); }}
                    disabled={!!result}
                  >
                    {choice.value}
                  </button>
                ))}
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Enter your answer..."
                disabled={!!result}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              />
            )}

            {!result && (
              <>
                <div className="action-buttons">
                  {question.hint_available && !hint && (
                    <button className="hint-btn" onClick={handleHint}>
                      💡 Hint
                    </button>
                  )}
                  <button className="submit-btn" onClick={handleSubmit}>
                    Check Answer
                  </button>
                </div>
                <div className="keyboard-hint">
                  Press <span className="kbd">?</span> for keyboard shortcuts
                </div>
              </>
            )}
          </div>

          {hint && (
            <div className="hint-box">
              <strong>Hint:</strong> <MathRenderer content={hint} />
            </div>
          )}

          {/* AI Help Panel - shows while working on question (for Socratic dialogue) */}
          {!result && (
            <AIHelpPanel
              questionId={question.question_id}
              studentId={STUDENT_ID}
              studentAnswer={answer}
              showAfterResult={false}
              questionPrompt={question.prompt}
            />
          )}

          {result && result.allow_retry && (
            <div className="result-box retry-mode">
              <div className="result-header">
                Not quite — try again!
              </div>

              {result.hint_text && (
                <div className="retry-hint">
                  <strong>Hint:</strong> <MathRenderer content={result.hint_text} />
                </div>
              )}

              <div className="retry-buttons">
                <button className="retry-btn" onClick={handleRetry}>
                  Try Again
                </button>
                <button className="show-answer-btn" onClick={handleShowAnswer}>
                  Show Answer
                </button>
              </div>
              <div className="keyboard-hint">
                Press <span className="kbd">Enter</span> to try again
              </div>
            </div>
          )}

          {result && !result.allow_retry && (
            <div className={`result-box ${result.correct ? 'correct' : 'incorrect'}`}>
              <div className="result-header">
                {result.correct
                  ? attemptNumber === 2
                    ? 'Got it on the second try!'
                    : 'Correct!'
                  : 'Incorrect'}
              </div>

              {!result.correct && result.correct_answer && (
                <div className="correct-answer">
                  The answer was: <strong>{result.correct_answer}</strong>
                </div>
              )}

              {result.explanation && (
                <div className="explanation">
                  <MathRenderer content={result.explanation} />
                </div>
              )}

              <div className="sr-feedback">
                {result.rating && (
                  <span className={`rating-badge rating-${result.rating.toLowerCase()}`}>
                    {result.rating}
                  </span>
                )}
                {result.next_review_date && (
                  <span className="next-review">
                    Next review: {formatReviewDate(result.next_review_date)}
                  </span>
                )}
              </div>

              <div className="result-actions">
                {!result.correct && (
                  <button className="similar-btn" onClick={fetchSimilarQuestion}>
                    Try Similar Problem
                  </button>
                )}
                <button className="next-btn" onClick={handleNext}>
                  Next Question →
                </button>
              </div>
              <div className="keyboard-hint">
                Press <span className="kbd">Enter</span> for next question
              </div>
            </div>
          )}

          {/* AI Help Panel - shows after getting a final result (not during retry) */}
          {result && !result.allow_retry && (
            <AIHelpPanel
              questionId={question.question_id}
              studentId={STUDENT_ID}
              studentAnswer={answer}
              showAfterResult={true}
              questionPrompt={question.prompt}
              correctAnswer={result.correct_answer || undefined}
            />
          )}

          {/* Feedback Widget - shows after incorrect answers */}
          {result && !result.allow_retry && !result.correct && (
            <FeedbackWidget
              questionId={question.question_id}
              studentId={STUDENT_ID}
            />
          )}
        </div>
      )}

      {/* Keyboard shortcuts help overlay */}
      {showShortcuts && (
        <div className="shortcuts-help">
          <h4>Keyboard Shortcuts</h4>
          <div className="shortcut-row">
            <span>Submit answer</span>
            <span className="kbd">Enter</span>
          </div>
          <div className="shortcut-row">
            <span>Get hint</span>
            <span className="kbd">H</span>
          </div>
          <div className="shortcut-row">
            <span>Toggle sound</span>
            <span className="kbd">S</span>
          </div>
          <div className="shortcut-row">
            <span>Show/hide shortcuts</span>
            <span className="kbd">?</span>
          </div>
          <div className="shortcut-row" style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
            Sound: {soundEnabled ? '🔊 On' : '🔇 Off'}
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticePage;
